import { UserProfileRole } from '@@types/api.types';
import { IntelTechnologiesPagination } from '@components/core/intel-technologies-table/intel-technologies-pagination.component';
import { IntelTechnologiesTable } from '@components/core/intel-technologies-table/intel-technologies-table.component';
import { MainLayout } from '@components/layouts/main.layout';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { useIntelTechnologiesQuery } from '@queries/intel-technologies.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import type { FC } from 'react';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { IntelTechnologiesFilterBar } from './components/intel-technologies-filter-bar.component';
import { useIntelTechnologiesCatalogQueryParams } from './hooks/use-intel-technologies-catalog-query-params.hook';

export const IntelTechnologiesPage: FC = () => {
  const { data: user, isPending: isUserPending } = useGetCurrentUser();

  const isIntel = user?.profile?.role === UserProfileRole.INTELLIGENCE;

  const {
    DEFAULT_SORTING,
    filtersState,
    handleFiltersChange,
    handleLimitChange,
    mrlMax,
    mrlMin,
    page,
    queryParams,
    search,
    setMrlMax,
    setMrlMin,
    setPage,
    setSearch,
    setSorting,
    setTrlMax,
    setTrlMin,
    sortingState,
    trlMax,
    trlMin,
  } = useIntelTechnologiesCatalogQueryParams();

  const {
    data: technologiesData,
    isFetching,
    isPending: isTechnologiesPending,
  } = useIntelTechnologiesQuery({
    ...queryParams,
    enabled: isIntel,
  });

  const technologies = technologiesData?.technologies ?? [];
  const pagination = technologiesData?.pagination;
  const hasScoreFilters = trlMin !== null || trlMax !== null || mrlMin !== null || mrlMax !== null;
  const hasCatalogFilters = filtersState.length > 0 || Boolean(search?.trim()) || hasScoreFilters;
  const showAccessEmptyState = !isTechnologiesPending && !hasCatalogFilters && pagination?.totalItems === 0;

  useEffect(() => {
    if (!pagination) return;

    const total = pagination.totalPages ?? 0;

    if (total < 1) {
      if (page !== 1) void setPage(1);
      return;
    }

    if (page > total) {
      void setPage(Math.max(1, total));
    }
  }, [page, pagination, setPage]);

  if (isUserPending) {
    return null;
  }

  if (!isIntel) {
    return <Navigate to={routes.home.path} />;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title="Intel Technologies" />
      <MainLayout fullWidth>
        <div className="mx-auto flex w-full max-w-(--breakpoint-lg) flex-col gap-4">
          <Tile header={{ title: 'Technologies' }}>
            <div className="flex flex-col gap-4">
              {!showAccessEmptyState && (
                <IntelTechnologiesFilterBar
                  filtersState={filtersState}
                  mrlMax={mrlMax}
                  mrlMin={mrlMin}
                  onFiltersChange={handleFiltersChange}
                  search={search}
                  setMrlMax={setMrlMax}
                  setMrlMin={setMrlMin}
                  setPage={setPage}
                  setSearch={setSearch}
                  setTrlMax={setTrlMax}
                  setTrlMin={setTrlMin}
                  trlMax={trlMax}
                  trlMin={trlMin}
                />
              )}

              {showAccessEmptyState ? (
                <EmptyStateContainer
                  className="min-h-72 rounded-xl border"
                  message="Ask an administrator to confirm your access"
                  title="No Technologies Available"
                />
              ) : (
                <>
                  <IntelTechnologiesTable
                    isLoading={isTechnologiesPending}
                    onSortingChange={(state) => {
                      if (!state || state.length === 0) {
                        void setSorting(DEFAULT_SORTING.map((item) => ({ ...item })));
                        return;
                      }
                      void setPage(1);
                      void setSorting(state);
                    }}
                    sorting={sortingState}
                    technologies={technologies}
                  />

                  {pagination && (
                    <IntelTechnologiesPagination
                      isFetching={isFetching}
                      pagination={pagination}
                      onPageChange={(nextPage) => void setPage(Math.max(1, nextPage))}
                      onPageSizeChange={handleLimitChange}
                    />
                  )}
                </>
              )}
            </div>
          </Tile>
        </div>
      </MainLayout>
    </PageviewTracker>
  );
};
